
import React, { useEffect, useRef } from 'react';
import { useAi } from '../context/AiContext';
import { useTranslation } from '../hooks/useTranslation';
import { AiProviderId, DEFAULT_MODELS, QUICK_ACTIONS } from '../lib/ai/types';
import { X, Send, Bot, Sparkles, Settings2, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWedding } from '../context/WeddingContext';
import { Badge } from './ui/Badge';

export const AiDrawer: React.FC = () => {
  const { isOpen, toggleDrawer, messages, config, updateConfig, sendMessage, isLoading, error, clearHistory, isDemoMode } = useAi();
  const { activeWedding } = useWedding();
  const { t } = useTranslation();
  const [input, setInput] = React.useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - Z-Index 1500 to sit above modals */}
      <div 
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[1500] transition-opacity"
        onClick={toggleDrawer}
      />

      {/* Drawer Panel - Z-Index 1600 */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[450px] bg-white dark:bg-slate-900 shadow-ds-card z-[1600] flex flex-col border-l border-slate-200 dark:border-slate-700 animate-fade-in">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-brand-600 text-white p-2 rounded-ds-button">
                <Sparkles size={20} />
              </div>
              <h2 className="text-ds-h3 text-slate-900 dark:text-white flex items-center gap-2">
                {t('ai.title')}
                <Badge variant={isDemoMode ? 'warning' : 'success'}>
                  {isDemoMode ? 'DEMO' : 'ACTIVE'}
                </Badge>
              </h2>
            </div>
            <div className="flex items-center gap-2">
               <button 
                 onClick={clearHistory}
                 className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-full transition-colors"
                 title={t('ai.clear_chat')}
               >
                 <Trash2 size={18} />
               </button>
               <button 
                 onClick={toggleDrawer}
                 className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-full transition-colors"
               >
                 <X size={20} />
               </button>
            </div>
          </div>

          {/* Configuration Toolbar */}
          <div className="flex flex-wrap gap-2 text-xs items-center">
             <select 
               className="bg-slate-100 dark:bg-slate-900 border-none rounded-lg px-2 py-1.5 font-medium text-slate-700 dark:text-slate-300 focus:ring-1 focus:ring-brand-500 outline-none cursor-pointer"
               value={config.provider}
               onChange={(e) => {
                 const newProvider = e.target.value as AiProviderId;
                 updateConfig({ 
                   provider: newProvider,
                   model: DEFAULT_MODELS[newProvider][0].id 
                 });
               }}
             >
               <option value="openai">OpenAI</option>
               <option value="anthropic">Anthropic</option>
               <option value="gemini">Gemini</option>
               <option value="custom">Custom</option>
             </select>

             <select 
               className="bg-slate-100 dark:bg-slate-900 border-none rounded-lg px-2 py-1.5 font-medium text-slate-700 dark:text-slate-300 focus:ring-1 focus:ring-brand-500 outline-none max-w-[120px] truncate cursor-pointer"
               value={config.model}
               onChange={(e) => updateConfig({ model: e.target.value })}
             >
               {DEFAULT_MODELS[config.provider].map(m => (
                 <option key={m.id} value={m.id}>{m.name}</option>
               ))}
             </select>

             <button 
               onClick={() => updateConfig({ includeContext: !config.includeContext })}
               className={`px-2 py-1.5 rounded-lg font-bold border transition-colors ${
                 config.includeContext 
                   ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 border-brand-200 dark:border-brand-900/30' 
                   : 'bg-slate-50 dark:bg-slate-900 text-slate-400 border-slate-100 dark:border-slate-800'
               }`}
               title="Wedding Context"
             >
               CTX
             </button>
             
             {/* Settings Link */}
             <Link 
               to={`/app/w/${activeWedding?.id}/settings`}
               onClick={toggleDrawer}
               className="ml-auto p-1.5 text-slate-400 hover:text-brand-500 transition-colors"
               title="Nastavení AI"
             >
               <Settings2 size={16} />
             </Link>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar bg-slate-50/50 dark:bg-[#0c0c0e]">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6 opacity-60">
               <div className="w-16 h-16 bg-gradient-to-br from-brand-400 to-pink-600 rounded-3xl flex items-center justify-center text-white mb-6 shadow-xl shadow-brand-500/20 animate-pulse">
                  <Sparkles size={32} />
               </div>
               <p className="text-sm font-medium text-slate-500 max-w-[250px]">{t('ai.empty_state')}</p>
               {isDemoMode && <p className="text-xs text-amber-500 font-bold mt-4 bg-amber-50 px-3 py-1 rounded-full">DEMO REŽIM (Bez API klíče)</p>}
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <div className={`
                  w-8 h-8 rounded-xl flex items-center justify-center shrink-0
                  ${msg.role === 'user' ? 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300' : 'bg-brand-600 text-white'}
                `}>
                  {msg.role === 'user' ? <div className="text-xs font-bold">JA</div> : <Bot size={16} />}
                </div>
                
                <div className={`
                  p-4 rounded-2xl text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap
                  ${msg.role === 'user' 
                    ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tr-none' 
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-tl-none border border-slate-100 dark:border-slate-800'}
                  ${msg.role === 'system' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 w-full' : ''}
                `}>
                  {msg.content}
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex gap-3">
               <div className="w-8 h-8 bg-brand-600 text-white rounded-xl flex items-center justify-center shrink-0">
                  <Loader2 size={16} className="animate-spin" />
               </div>
               <div className="bg-slate-100 dark:bg-slate-900 px-4 py-3 rounded-2xl rounded-tl-none text-xs font-bold text-slate-500 animate-pulse">
                  {t('ai.typing')}
               </div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 p-4 rounded-xl text-sm font-bold flex items-center gap-3 border border-red-100 dark:border-red-900/30">
               <AlertTriangle size={18} />
               {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions & Input */}
        <div className="p-4 bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800 space-y-4">
           {/* Quick Actions */}
           <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
              {QUICK_ACTIONS.map(action => (
                <button
                  key={action.id}
                  onClick={() => sendMessage(action.prompt)}
                  disabled={isLoading}
                  className="whitespace-nowrap px-3 py-1.5 bg-slate-100 dark:bg-slate-900 hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:text-brand-600 text-slate-600 dark:text-slate-400 rounded-lg text-xs font-bold transition-colors border border-transparent hover:border-brand-200 dark:hover:border-brand-900/30 disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
           </div>

           {/* Input */}
           <div className="relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('ai.placeholder')}
                rows={1}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl pl-4 pr-12 py-4 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none custom-scrollbar"
                style={{ minHeight: '56px', maxHeight: '120px' }}
              />
              <button 
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="absolute right-2 top-2 p-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send size={18} />
              </button>
           </div>
        </div>
      </div>
    </>
  );
};
